import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import {
  FiTrendingUp, FiShield, FiActivity, FiMapPin,
  FiArrowUpRight, FiChevronDown, FiAlertTriangle, FiCpu, FiLock
} from 'react-icons/fi';

/* ── Neon Stat Ticker ── */
const NeonTicker = () => {
  const ticks = [
    'NPA RATE ↓ 2.3%', 'RECOVERY RATE ↑ 94.8%', 'ACTIVE LOANS ↑ 12,440',
    'DISBURSALS TODAY ₹4.2CR', 'BUREAU HITS 0 ERRORS', 'UPTIME 99.99%'
  ];
  const doubled = [...ticks, ...ticks];
  return (
    <div className="overflow-hidden bg-[#00ff88]/10 border-y border-[#00ff88]/20 py-3">
      <motion.div className="flex gap-12 whitespace-nowrap"
        animate={{ x: ['0%', '-50%'] }} transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}>
        {doubled.map((t, i) => (
          <span key={i} className="text-xs font-medium tracking-widest uppercase text-[#00ff88] flex items-center gap-3">
            <span className="text-[#00ff88]/40">◆</span> {t}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

/* ── Animated CountUp stat ── */
const NeonStat = ({ value, suffix, label }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl font-medium text-[#00ff88] tabular-nums">
        {inView ? <CountUp end={value} duration={2.5} suffix={suffix} separator="," /> : '0'}
      </p>
      <p className="text-gray-400 text-xs uppercase tracking-widest mt-1">{label}</p>
    </div>
  );
};

/* ── FAQ ── */
const Faq = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className={`border rounded-2xl overflow-hidden transition-colors ${open === i ? 'border-[#00ff88]/50 bg-[#00ff88]/5' : 'border-gray-800 bg-[#111827]'}`}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
            <span className={`font-medium ${open === i ? 'text-[#00ff88]' : 'text-gray-200'}`}>{f.q}</span>
            <FiChevronDown className={`transition-transform duration-300 ${open === i ? 'rotate-180 text-[#00ff88]' : 'text-gray-500'}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                <p className="p-5 pt-2 text-gray-400 border-t border-gray-800">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════ MAIN PAGE ═══════════════════════════════════════ */
export default function AutoFinance() {
  useEffect(() => window.scrollTo(0, 0), []);

  const kpis = [
    { icon: <FiTrendingUp />, value: '94.8%', label: 'Recovery Rate', delta: '+2.1% MoM', good: true },
    { icon: <FiAlertTriangle />, value: '1.4%', label: 'NPA Rate', delta: '−0.3%', good: true },
    { icon: <FiActivity />, value: '12,440', label: 'Active Loans', delta: '+482 new', good: true },
    { icon: <FiShield />, value: '99.99%', label: 'Platform Uptime', delta: 'SLA guaranteed', good: true },
  ];

  const aiFeatures = [
    { icon: <FiCpu />, title: 'AI Underwriting', desc: 'Bureau + bank statement + alternate data — risk score in 60 seconds.' },
    { icon: <FiMapPin />, title: 'Field Agent App', desc: 'Geo-tagged collections, offline sync, Bluetooth receipt printing.' },
    { icon: <FiActivity />, title: 'EMI Engine', desc: 'Flat, reducing balance, Rule of 78, moratorium — fully configurable.' },
    { icon: <FiLock />, title: 'RBI Compliance', desc: '256-bit AES + TLS 1.3. Fully compliant with RBI digital lending norms.' },
    { icon: <FiShield />, title: 'Fraud Detection', desc: 'Real-time anomaly flagging on applications and repayment behaviour.' },
    { icon: <FiTrendingUp />, title: 'Portfolio Analytics', desc: 'Cohort analysis, vintage curves, and bucket-wise collection dashboards.' },
  ];

  const steps = [
    { num: '01', title: 'Application', desc: 'Digital KYC + vehicle details submitted by customer or dealer.' },
    { num: '02', title: 'AI Underwriting', desc: 'Bureau pull, bank analysis, and alternate data — scored instantly.' },
    { num: '03', title: 'Sanction', desc: 'Auto-approve within band; edge cases route to human underwriters.' },
    { num: '04', title: 'Disbursement', desc: 'Funds transferred to dealer/customer via NEFT/IMPS within 2 hours.' },
    { num: '05', title: 'Collections', desc: 'Automated EMI reminders → field agent routing → NPA provisioning.' },
  ];

  const faqs = [
    { q: 'How does AI underwriting reduce NPAs?', a: 'Our model analyzes 200+ data points including repayment history, cash flow patterns, and social signals to generate a predictive default score within 60 seconds.' },
    { q: 'Can agents use the app in low-connectivity areas?', a: 'Yes — the field app fully supports offline operation. Collections logged offline sync automatically once connectivity is restored.' },
    { q: 'Which vehicle types are supported?', a: '2-wheelers, 3-wheelers, passenger cars, commercial vehicles, tractors, and EVs — each with separate risk and rate configurations.' },
    { q: 'Is the platform RBI DLGG compliant?', a: 'Yes, we comply fully with RBI\'s Digital Lending Guidelines including disclosure requirements, limit management, and Lending Service Provider regulations.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a12] font-mono text-white">
      <div className="bg-black border-b border-gray-900"><Header /></div>

      {/* ── HERO: Dark Fintech ── */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32">
        {/* Animated dot grid background */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, rgba(0,255,136,0.07) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}></div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-[#0a0a12]"></div>
        {/* Glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#00ff88]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-[#00ff88] uppercase mb-8 px-4 py-2 border border-[#00ff88]/30 rounded-full bg-[#00ff88]/5">
              <span className="w-2 h-2 bg-[#00ff88] rounded-full animate-pulse"></span> Finance Intelligence Platform
            </span>
            <h1 className="text-6xl lg:text-8xl font-medium leading-none mb-6 tracking-tight">
              LEND SMARTER.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#00ccff]">COLLECT FASTER.</span>
            </h1>
            <p className="text-gray-400 font-medium text-xl max-w-2xl mx-auto mb-12 leading-relaxed font-sans">
              The only finance platform with AI underwriting, real-time field collections, and RBI-compliant loan management — built for NBFCs and HFCs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products#DemoForm">
                <button className="bg-[#00ff88] text-black font-medium px-10 py-4 rounded-lg hover:bg-[#00cc70] transition-all shadow-[0_0_30px_rgba(0,255,136,0.3)] text-sm tracking-wider">
                  SCHEDULE DEMO
                </button>
              </Link>
              <Link
  to="https://finpilot.vivekasoftek.com/"
  target="_blank"
  rel="noopener noreferrer"
  className="flex items-center gap-2 border border-[#00ff88]/30 text-[#00ff88] font-medium px-10 py-4 rounded-lg hover:bg-[#00ff88]/10 transition-all text-sm tracking-wider"
>
  START NOW
  <FiArrowUpRight />
</Link>
            </div>
          </motion.div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20">
            {kpis.map((k, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                className="bg-white/5 border border-[#00ff88]/20 rounded-2xl p-6 text-left hover:border-[#00ff88]/50 hover:bg-[#00ff88]/5 transition-all group">
                <div className="text-[#00ff88] text-2xl mb-4 group-hover:scale-110 transition-transform">{k.icon}</div>
                <p className="text-2xl font-medium text-white mb-1 font-mono">{k.value}</p>
                <p className="text-gray-400 text-xs uppercase tracking-wider">{k.label}</p>
                <p className="text-[#00ff88] text-xs font-medium mt-2">{k.delta}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEON TICKER ── */}
      <div className="my-10">
        <NeonTicker />
      </div>

      {/* ── STATS BAR ── */}
      <section className="py-16 bg-[#0d1117] border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
          {[
            { value: 5000, suffix: 'Cr+', label: 'Loans Disbursed' },
            { value: 94.8, suffix: '%', label: 'Recovery Rate' },
            { value: 60, suffix: 'sec', label: 'Underwriting Speed' },
            { value: 99.99, suffix: '%', label: 'Uptime SLA' },
          ].map((s, i) => <NeonStat key={i} {...s} />)}
        </div>
      </section>

      {/* ── AI FEATURE GRID ── */}
      <section className="py-20 bg-[#0a0a12]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-14">
            <h2 className="text-4xl font-medium text-white mb-3">
              AI-powered. <span className="text-[#00ff88]">NPA-proof.</span>
            </h2>
            <p className="text-gray-400 text-lg font-sans max-w-xl">Six core modules engineered to maximise collections and minimise risk from day one.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {aiFeatures.map((f, i) => (
              <motion.div key={i} whileHover={{ borderColor: '#00ff88', backgroundColor: 'rgba(0,255,136,0.04)' }}
                className="border border-gray-800 rounded-2xl p-7 transition-all group cursor-pointer">
                <div className="text-[#00ff88] text-3xl mb-5 group-hover:scale-110 transition-transform">{f.icon}</div>
                <h3 className="text-lg font-medium text-white mb-2 font-mono">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed font-sans">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VERTICAL DARK TIMELINE ── */}
      <section className="py-20 bg-[#0d1117]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-medium text-white mb-14 text-center">Loan lifecycle, <span className="text-[#00ff88]">automated end-to-end.</span></h2>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#00ff88] to-transparent"></div>
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex gap-8 mb-10 last:mb-0">
                <div className="w-16 h-16 shrink-0 rounded-xl bg-[#0a0a12] border border-[#00ff88]/40 flex items-center justify-center font-medium text-[#00ff88] text-sm z-10">{s.num}</div>
                <div className="pt-2">
                  <h4 className="text-white font-medium text-lg mb-1">{s.title}</h4>
                  <p className="text-gray-400 font-sans">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BANNER ── */}
      <section className="relative py-24 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1920&q=80" alt="Finance" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a12]/85"></div>
        <div className="relative z-10 text-center px-6">
          <h2 className="text-4xl font-medium text-white mb-4">Over <span className="text-[#00ff88]">₹5,000 Crore</span> disbursed through our platform.</h2>
          <div className="flex flex-wrap justify-center gap-8 mt-8 text-gray-500 font-medium text-lg uppercase tracking-widest">
            {['NBFCs','HFCs','Co-operative Banks','MFIs'].map(t => <span key={t}>{t}</span>)}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#0a0a12]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-medium text-white mb-10 text-center font-mono">FAQ_</h2>
          <Faq faqs={faqs} />
        </div>
      </section>

      <div className="bg-black border-t border-gray-900 text-white"><Footer /></div>
    </div>
  );
}
